import { useEffect, useMemo, useRef, useState } from 'react';

type Anchor = 'start' | 'middle' | 'end';
type Stop = {
  name: string;
  x: number;
  y: number;
  labelX: number;
  labelY: number;
  anchor: Anchor;
  arrivalYear: number;
  departureYear: number;
};

const stops: Stop[] = [
  { name: 'Ocala', x: 70.4, y: 28.5, labelX: 72.6, labelY: 26.6, anchor: 'start', arrivalYear: 1956, departureYear: 1979 },
  { name: 'Panacea', x: 41.9, y: 15.9, labelX: 38.8, labelY: 18.8, anchor: 'end', arrivalYear: 1979, departureYear: 1981 },
  { name: 'Crawfordville', x: 42.1, y: 13.8, labelX: 39.3, labelY: 11.5, anchor: 'end', arrivalYear: 1981, departureYear: 1984 },
  { name: 'Mount Plymouth', x: 78.1, y: 34.2, labelX: 80.4, labelY: 37.1, anchor: 'start', arrivalYear: 1985, departureYear: 1992 },
  { name: 'Eustis', x: 76.1, y: 33.5, labelX: 73.7, labelY: 31.6, anchor: 'end', arrivalYear: 1992, departureYear: 1998 },
  { name: 'Mt. Dora', x: 76.7, y: 34.5, labelX: 74.3, labelY: 37.8, anchor: 'end', arrivalYear: 1998, departureYear: 2004 },
  { name: 'Alachua', x: 66.2, y: 23.15, labelX: 63.9, labelY: 21.1, anchor: 'end', arrivalYear: 2004, departureYear: 2026 },
];

type Point = { x: number; y: number };
type RouteSegment = { from: Stop; to: Stop; c1: Point; c2: Point };

const segments: RouteSegment[] = [
  { from: stops[0], to: stops[1], c1: { x: 64.5, y: 31.5 }, c2: { x: 52.5, y: 12.4 } },
  { from: stops[1], to: stops[2], c1: { x: 39.7, y: 15.4 }, c2: { x: 44.1, y: 14.5 } },
  { from: stops[2], to: stops[3], c1: { x: 51.2, y: 10.8 }, c2: { x: 66.5, y: 38.7 } },
  { from: stops[3], to: stops[4], c1: { x: 80.1, y: 32.2 }, c2: { x: 74.2, y: 35.6 } },
  { from: stops[4], to: stops[5], c1: { x: 74.5, y: 32.7 }, c2: { x: 78.4, y: 35.6 } },
  { from: stops[5], to: stops[6], c1: { x: 76.1, y: 29.9 }, c2: { x: 68.1, y: 27.3 } },
];

function pointOnCurve(segment: RouteSegment, t: number) {
  const inverse = 1 - t;
  const x = inverse ** 3 * segment.from.x + 3 * inverse ** 2 * t * segment.c1.x + 3 * inverse * t ** 2 * segment.c2.x + t ** 3 * segment.to.x;
  const y = inverse ** 3 * segment.from.y + 3 * inverse ** 2 * t * segment.c1.y + 3 * inverse * t ** 2 * segment.c2.y + t ** 3 * segment.to.y;
  const dx = 3 * inverse ** 2 * (segment.c1.x - segment.from.x) + 6 * inverse * t * (segment.c2.x - segment.c1.x) + 3 * t ** 2 * (segment.to.x - segment.c2.x);
  return { x, y, facing: (dx < 0 ? -1 : 1) as 1 | -1 };
}

const truck = 'M363.8 96l57.6 96-133.4 0 0-96 75.8 0zM496 192L418.6 63.1C407.1 43.8 386.2 32 363.8 32L256 32c-17.7 0-32 14.3-32 32l0 128-144 0c-26.5 0-48 21.5-48 48l0 80c-17.7 0-32 14.3-32 32s14.3 32 32 32l32.4 0c-.2 2.6-.4 5.3-.4 8 0 48.6 39.4 88 88 88s88-39.4 88-88c0-2.7-.1-5.4-.4-8l160.7 0c-.2 2.6-.4 5.3-.4 8 0 48.6 39.4 88 88 88s88-39.4 88-88c0-2.7-.1-5.4-.4-8l32.4 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l0-80c0-26.5-21.5-48-48-48l-64 0zM112 392a40 40 0 1 1 80 0 40 40 0 1 1-80 0zm376-40a40 40 0 1 1 0 80 40 40 0 1 1 0-80z';
const MAP_REVEAL_DELAY = 1250;
const TRAVEL_TIME = 900;
const YEAR_TIME = 95;
const MINIMUM_DWELL = 480;

type Motion = { x: number; y: number; year: number; facing: 1 | -1; activeSegment: number; segmentProgress: number; revealed: number; moving: boolean; started: boolean };

function easeInOutCubic(value: number) {
  return value < .5 ? 4 * value ** 3 : 1 - (-2 * value + 2) ** 3 / 2;
}

export function FloridaJourney({ onYearChange }: { onYearChange?: (year: number) => void }) {
  const routes = useMemo(() => segments.map((segment) => `M${segment.from.x} ${segment.from.y} C${segment.c1.x} ${segment.c1.y} ${segment.c2.x} ${segment.c2.y} ${segment.to.x} ${segment.to.y}`), []);
  const routeRefs = useRef<Array<SVGPathElement | null>>([]);
  const [motion, setMotion] = useState<Motion>(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const last = stops.at(-1)!;
      return { x: last.x, y: last.y, year: 2026, facing: 1, activeSegment: segments.length - 1, segmentProgress: 1, revealed: stops.length - 1, moving: false, started: true };
    }
    return { x: stops[0].x, y: stops[0].y, year: 1956, facing: 1, activeSegment: -1, segmentProgress: 0, revealed: -1, moving: false, started: false };
  });

  useEffect(() => {
    onYearChange?.(motion.year);
  }, [motion.year, onYearChange]);

  useEffect(() => {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    let frame = 0;
    const started = performance.now() + MAP_REVEAL_DELAY;

    const tick = (now: number) => {
      const elapsed = Math.max(0, now - started);
      if (now < started) {
        frame = requestAnimationFrame(tick);
        return;
      }

      let cursor = elapsed;
      for (let index = 0; index < stops.length; index += 1) {
        const stop = stops[index];
        const residenceYears = stop.departureYear - stop.arrivalYear;
        const dwellTime = Math.max(MINIMUM_DWELL, residenceYears * YEAR_TIME);
        if (cursor <= dwellTime) {
          const t = Math.max(0, cursor / dwellTime);
          const incoming = index > 0 ? pointOnCurve(segments[index - 1], 1) : { facing: 1 as const };
          setMotion({
            x: stop.x,
            y: stop.y,
            year: Math.round(stop.arrivalYear + residenceYears * t),
            facing: incoming.facing,
            activeSegment: index - 1,
            segmentProgress: index > 0 ? 1 : 0,
            revealed: index,
            moving: false,
            started: true,
          });
          frame = requestAnimationFrame(tick);
          return;
        }
        cursor -= dwellTime;

        if (index < segments.length && cursor <= TRAVEL_TIME) {
          const segment = segments[index];
          const t = Math.max(0, cursor / TRAVEL_TIME);
          const eased = easeInOutCubic(t);
          const route = routeRefs.current[index];
          const point = (() => {
            if (!route) return pointOnCurve(segment, eased);
            const length = route.getTotalLength();
            const distance = length * eased;
            const position = route.getPointAtLength(distance);
            const tangentStart = route.getPointAtLength(Math.max(0, distance - .08));
            const tangentEnd = route.getPointAtLength(Math.min(length, distance + .08));
            return { x: position.x, y: position.y, facing: (tangentEnd.x < tangentStart.x ? -1 : 1) as 1 | -1 };
          })();
          setMotion({
            x: point.x,
            y: point.y,
            year: Math.round(stop.departureYear + (stops[index + 1].arrivalYear - stop.departureYear) * eased),
            facing: point.facing,
            activeSegment: index,
            segmentProgress: eased,
            revealed: index + 1,
            moving: true,
            started: true,
          });
          frame = requestAnimationFrame(tick);
          return;
        }
        if (index < segments.length) {
          cursor -= TRAVEL_TIME;
        }
      }

      const last = stops.at(-1)!;
      setMotion({ x: last.x, y: last.y, year: 2026, facing: 1, activeSegment: segments.length - 1, segmentProgress: 1, revealed: stops.length - 1, moving: false, started: true });
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return <div className="florida-journey" aria-label="Jeff's Florida journey: Ocala, Panacea, Crawfordville, Mount Plymouth, Eustis, Mount Dora, and Alachua">
    <div className="florida-map-stage"><svg className={motion.started ? 'journey-started' : ''} viewBox="0 0 100 100" role="img" aria-hidden="true">
      <image className="florida-silhouette" href="/assets/florida-outline.svg" x="0" y="0" width="100" height="100" preserveAspectRatio="xMidYMid meet" />
      {routes.map((route, index) => {
        const progress = index < motion.activeSegment ? 1 : index === motion.activeSegment ? motion.segmentProgress : 0;
        return <g className={`route-segment ${progress > .001 ? 'is-visible' : ''}`} key={route}>
          <path className="route-line route-halo" pathLength="1" d={route} style={{ strokeDashoffset: 1 - progress }} />
          <path ref={(element) => { routeRefs.current[index] = element; }} className="route-line" pathLength="1" d={route} style={{ strokeDashoffset: 1 - progress }} />
        </g>;
      })}
      {stops.map((stop, index) => <g className={`map-stop ${index <= motion.revealed ? 'is-revealed' : ''}`} key={stop.name}>
        <circle cx={stop.x} cy={stop.y} r="1.5" /><circle className="stop-core" cx={stop.x} cy={stop.y} r=".55" />
        <text x={stop.labelX} y={stop.labelY} textAnchor={stop.anchor}>{stop.name}</text>
      </g>)}
      <g className={`route-vehicle ${motion.moving ? 'is-moving' : ''} ${motion.revealed === stops.length - 1 && !motion.moving ? 'is-resting' : ''}`} transform={`translate(${motion.x} ${motion.y}) scale(${motion.facing} 1)`}>
        <g transform="translate(-2.1 -1.65) scale(.0082)"><path d={truck} /></g>
      </g>
    </svg></div>
  </div>;
}
